import { useState } from 'react';
import { toast } from 'sonner';
import {
  useExpertTipTopics,
  useCreateExpertTipTopic,
  useUpdateExpertTipTopic,
  useUpdateExpertTipTopicStatus,
} from '../hooks/useExpertTipTopics';
import type { ExpertTipTopic } from '../types/expert-tip-topic';

export default function ExpertTipTopicsPage() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [newTopicTitle, setNewTopicTitle] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const { data, isLoading } = useExpertTipTopics({ page, pageSize });
  const createMutation = useCreateExpertTipTopic();
  const updateMutation = useUpdateExpertTipTopic();
  const statusMutation = useUpdateExpertTipTopicStatus();

  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  const handleCreate = async () => {
    if (!newTopicTitle.trim()) return;
    try {
      await createMutation.mutateAsync({ title: newTopicTitle.trim() });
      setNewTopicTitle('');
      setShowCreateForm(false);
      toast.success('Topic created');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create topic');
    }
  };

  const handleEditStart = (topic: ExpertTipTopic) => {
    setEditingId(topic.id.toString());
    setEditTitle(topic.title);
  };

  const handleEditSave = async () => {
    if (!editingId || !editTitle.trim()) return;
    try {
      await updateMutation.mutateAsync({ id: editingId, data: { title: editTitle.trim() } });
      setEditingId(null);
      toast.success('Topic updated');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to update topic');
    }
  };

  const handleEditCancel = () => {
    setEditingId(null);
    setEditTitle('');
  };

  const handleArchive = async (topic: ExpertTipTopic) => {
    const action = topic.isDeleted ? 'restore' : 'archive';
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} topic "${topic.title}"?`)) return;
    try {
      await statusMutation.mutateAsync({ id: topic.id.toString(), isDeleted: !topic.isDeleted });
      toast.success(`Topic ${action}d`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Failed to ${action} topic`);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-heading-1 text-admin-slate-400">Tip Topics</h1>
        <button
          onClick={() => setShowCreateForm((prev) => !prev)}
          className="admin-button-primary"
        >
          {showCreateForm ? 'Cancel' : 'New Topic'}
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-admin-dark-800 rounded-lg border border-admin-slate-300/10 p-4 flex gap-3">
          <input
            type="text"
            value={newTopicTitle}
            onChange={(e) => setNewTopicTitle(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            placeholder="Topic title..."
            maxLength={100}
            className="flex-1 px-3 py-2 rounded-lg border border-admin-slate-300/20 bg-admin-dark-700 text-admin-slate-300"
            autoFocus
          />
          <button
            onClick={handleCreate}
            disabled={!newTopicTitle.trim() || createMutation.isPending}
            className="admin-button-primary disabled:opacity-50"
          >
            {createMutation.isPending ? 'Creating...' : 'Create'}
          </button>
        </div>
      )}

      <div className="bg-admin-dark-800 rounded-lg border border-admin-slate-300/10 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-admin-slate-300/10">
              <th className="px-4 py-3 text-left text-xs font-medium text-admin-slate-500 uppercase tracking-wider">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-admin-slate-500 uppercase tracking-wider">Published Tips</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-admin-slate-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-admin-slate-500 uppercase tracking-wider">Created</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-admin-slate-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-admin-slate-300/10">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-admin-slate-500">Loading...</td>
              </tr>
            )}
            {!isLoading && data?.items.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-admin-slate-500">No topics yet</td>
              </tr>
            )}
            {data?.items.map((topic) => (
              <tr key={topic.id} className={`hover:bg-admin-dark-700/50 ${topic.isDeleted ? 'opacity-50' : ''}`}>
                <td className="px-4 py-3 text-admin-slate-300">
                  {editingId === topic.id.toString() ? (
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleEditSave();
                        if (e.key === 'Escape') handleEditCancel();
                      }}
                      maxLength={100}
                      className="w-full px-2 py-1 rounded border border-admin-slate-300/20 bg-admin-dark-700 text-admin-slate-300"
                      autoFocus
                    />
                  ) : (
                    topic.title
                  )}
                </td>
                <td className="px-4 py-3 text-admin-slate-400">{topic.publishedTipCount}</td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                    topic.isDeleted
                      ? 'bg-red-900/30 text-red-400'
                      : 'bg-green-900/30 text-green-400'
                  }`}>
                    {topic.isDeleted ? 'Archived' : 'Active'}
                  </span>
                </td>
                <td className="px-4 py-3 text-admin-slate-500 text-sm">
                  {new Date(topic.createdAt).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-right">
                  {editingId === topic.id.toString() ? (
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={handleEditSave}
                        disabled={updateMutation.isPending}
                        className="px-3 py-1 text-xs rounded bg-admin-accent-blue text-white hover:bg-admin-accent-blue/80 disabled:opacity-50"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleEditCancel}
                        className="px-3 py-1 text-xs rounded border border-admin-slate-300/20 text-admin-slate-400 hover:bg-admin-dark-700"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2 justify-end">
                      <button
                        onClick={() => handleEditStart(topic)}
                        className="px-3 py-1 text-xs rounded border border-admin-slate-300/20 text-admin-slate-400 hover:bg-admin-dark-700"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleArchive(topic)}
                        disabled={statusMutation.isPending}
                        className="px-3 py-1 text-xs rounded border border-admin-slate-300/20 text-admin-slate-400 hover:bg-admin-dark-700 disabled:opacity-50"
                      >
                        {topic.isDeleted ? 'Restore' : 'Archive'}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-4 py-2 text-sm rounded border border-admin-slate-300/20 text-admin-slate-400 hover:bg-admin-dark-700 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-admin-slate-500">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-4 py-2 text-sm rounded border border-admin-slate-300/20 text-admin-slate-400 hover:bg-admin-dark-700 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
